import { useEffect, useRef, type Dispatch } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { genUuid } from '@/lib/uuid';
import { SectionCard } from '../../anketa/components/SectionCard';
import { SectionHeader } from '../../anketa/components/SectionHeader';
import { DEFAULT_CONTACTS, type ContactField } from '../contactRows';
import type { QuoteAction } from '../quoteReducer';
import type { ContactRow, QuoteState } from '../types';

/**
 * Block 12 PR-D-A Phase D4 Step 6 + PR-H7 (ADR-0030 §8) — раздел «Контакты
 * исполнителя».
 *
 * 2 default-строки (fio + phone) + custom additions через «Добавить строку».
 * Прототип `docs/prototypes/screen-2.12.10-cp.html` lines 283-318.
 *
 * **PR-H7 (ADR-0030 §8):** убрана 3-я default-строка «Название мебели»
 * (`furniture-name`) — Aibek decision 2026-05-28: instance name не используется
 * на практике, дублирует contract-side responsibility. Legacy drafts с
 * 3-contact array — `furniture-name` row отфильтрована HYDRATE reducer'ом
 * (non-destructive — IDB record не переписывается).
 *
 * **Auth auto-fill deferred (BACKLOG B-119, never opened):** `useAuth`
 * отсутствует в проекте (verified pre-flight 2026-05-17). При появлении
 * hook'а — добавить initial derivation phone/fio в Phase D5 либо отдельный fix.
 *
 * **Layout (mirror SectionConditions pattern):**
 *  - default row: stacked label + input
 *  - custom row: inline label input + value input + trash
 *
 * **Тел/ФИО — text inputs** (mobile phone validation скип — пользователи
 * вводят произвольный формат +7..., 8-..., '+7 (777)' и т.п.).
 */
export interface SectionExecutorContactsProps {
  state: QuoteState;
  dispatch: Dispatch<QuoteAction>;
}

const DEFAULT_ROW_IDS: ReadonlySet<string> = new Set(DEFAULT_CONTACTS.map((r) => r.id));

export function SectionExecutorContacts({ state, dispatch }: SectionExecutorContactsProps) {
  const rows = state.executorContacts;

  // One-time mount init (mirror SectionConditions). delete-to-zero не должен
  // re-init'ить defaults.
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    if (rows.length === 0) dispatch({ type: 'CONTACT_INIT_DEFAULTS' });
  }, [rows.length, dispatch]);

  const handleUpdate = (id: string, field: ContactField, value: string) => {
    dispatch({ type: 'CONTACT_UPDATE', id, field, value });
  };
  const handleAdd = () => {
    dispatch({ type: 'CONTACT_ADD_CUSTOM', id: genUuid() });
  };
  const handleDelete = (id: string) => {
    dispatch({ type: 'CONTACT_DELETE', id });
  };

  return (
    <SectionCard testId="section-executor-contacts">
      <SectionHeader>Контакты исполнителя</SectionHeader>
      <ul role="list" className="flex flex-col gap-3" data-testid="contacts-list">
        {rows.map((row) =>
          DEFAULT_ROW_IDS.has(row.id) ? (
            <DefaultContactRow key={row.id} row={row} onUpdate={handleUpdate} />
          ) : (
            <CustomContactRow
              key={row.id}
              row={row}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ),
        )}
      </ul>

      <button
        type="button"
        onClick={handleAdd}
        aria-label="Добавить контакт"
        data-testid="contacts-add-row"
        className="w-full min-h-12 mt-3 rounded-xl border-2 border-dashed border-accent bg-card text-ink text-base font-semibold flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
      >
        <Plus size={18} aria-hidden="true" />
        Добавить строку
      </button>
    </SectionCard>
  );
}

const INPUT_CLASS =
  'w-full min-h-12 px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none';

interface DefaultContactRowProps {
  row: ContactRow;
  onUpdate: (id: string, field: ContactField, value: string) => void;
}

function DefaultContactRow({ row, onUpdate }: DefaultContactRowProps) {
  const fieldId = `quote-contact-${row.id}`;
  return (
    <li>
      <label htmlFor={fieldId} className="block text-sm font-semibold text-ink/80 mb-1">
        {row.label}
      </label>
      <input
        id={fieldId}
        type="text"
        value={row.value}
        onChange={(e) => onUpdate(row.id, 'value', e.target.value)}
        aria-label={row.label}
        data-testid={`contact-${row.id}-input`}
        className={INPUT_CLASS}
      />
    </li>
  );
}

interface CustomContactRowProps {
  row: ContactRow;
  onUpdate: (id: string, field: ContactField, value: string) => void;
  onDelete: (id: string) => void;
}

function CustomContactRow({ row, onUpdate, onDelete }: CustomContactRowProps) {
  const rowName = row.label.trim() || 'без названия';
  return (
    <li className="flex items-end gap-1.5">
      <div className="flex-1 min-w-0">
        <label className="block text-xs font-semibold text-muted mb-1">Название</label>
        <input
          type="text"
          value={row.label}
          onChange={(e) => onUpdate(row.id, 'label', e.target.value)}
          placeholder="Например: Telegram"
          aria-label={`Название контакта: ${rowName}`}
          className={INPUT_CLASS}
        />
      </div>
      <div className="flex-1 min-w-0">
        <label className="block text-xs font-semibold text-muted mb-1">Значение</label>
        <input
          type="text"
          value={row.value}
          onChange={(e) => onUpdate(row.id, 'value', e.target.value)}
          placeholder="—"
          aria-label={`Значение контакта: ${rowName}`}
          className={INPUT_CLASS}
        />
      </div>
      <button
        type="button"
        onClick={() => onDelete(row.id)}
        aria-label={`Удалить контакт ${rowName}`}
        className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md text-destructive focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
      >
        <Trash2 size={18} aria-hidden="true" />
      </button>
    </li>
  );
}
